/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight,
  Shield,
  Clock,
  Users,
  Award,
  MapPin,
  Star,
  Phone,
  MessageCircle,
} from "lucide-react";
import axios from "axios";
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [destinations, setDestinations] = useState({ international: [], domestic: [] });
  const [packages, setPackages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [trendingPkgs, setTrendingPkgs] = useState([]);
  const navigate = useNavigate();

  const scrollToTop = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [destRes, pkgRes, reviewRes, trendRes] = await Promise.all([
          axios.get(`${API}/destinations`),
          axios.get(`${API}/packages`),
          axios.get(`${API}/reviews`).catch(() => ({ data: [] })),
          axios.get(`${API}/packages/trending`).catch(() => ({ data: [] })),
        ]);
        setDestinations(destRes.data);
        setPackages(pkgRes.data.slice(0, 6));
        setReviews(reviewRes.data);
        setTrendingPkgs(trendRes.data.slice(0, 4));
      } catch (e) {
        logger.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  const trustFactors = [
    { icon: Shield, title: "100% Secure", description: "Safe & secure bookings with trusted payment gateways" },
    { icon: Clock, title: "24/7 Support", description: "Round the clock assistance for all your travel needs" },
    { icon: Users, title: "10,000+ Happy Travelers", description: "Trusted by thousands of satisfied customers" },
    { icon: Award, title: "8+ Years Experience", description: "Industry expertise you can rely on" },
  ];

  const testimonials = [
    {
      name: "Prachi Jain",
      location: "Mumbai",
      text: "Seamless and Memorable Europe Trip! We booked our Europe trip with DNO and couldn't be happier with the experience. The itinerary was thoughtfully planned, incredibly smooth, and included well-paced leisure days that made the journey even more enjoyable. Every detail was taken care of, allowing us to travel stress-free and soak in the beauty of Europe. Loved every moment — highly recommend DNO for anyone looking for a perfectly curated travel experience!",
      rating: 5,
      initial: "P",
    },
    {
      name: "Nidhi Rajkotia",
      location: "India",
      text: "Great experience with Dnotours and Travel during our 6-day trip to Jodhpur, Pushkar, Khatushyam Ji, and Jaipur. We traveled with an infant, and everything was managed smoothly. Mr. Mihir was available 24/7 for any queries and always gave quick responses, which was very reassuring. They suggested good, budget-friendly hotels near major attractions, and the trip was well organised with a polite and cooperative driver. Highly recommended for a stress-free journey!",
      rating: 4.5,
      initial: "N",
    },
    {
      name: "Roma Salian",
      location: "India",
      text: "My sister & I have taken multiple trips with DNO (most recent was Dubai) and honestly, they were excellent every single time. They will help you end-to-end with your dream trips and will show up with best recommendations and cost effective solutions. Highly recommend to book your next trip with them!",
      rating: 5,
      initial: "R",
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758717152007-6a2eb7299409?w=1920&q=80"
            alt="Luxury Travel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
        </div>

        <div className="container-custom relative z-10 pt-20">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-2 bg-white/20 text-white rounded-full text-sm font-medium mb-6">
                Premium Travel Experiences
              </span>
              <h1 className="heading-1 text-white mb-6">
                Discover the World with{" "}
                <span className="text-coral">Dnotours</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
                Your trusted partner for crafting unforgettable journeys. From exotic international destinations to incredible domestic escapes, we make every trip extraordinary.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => scrollToTop("/get-quote")}
                  className="btn-coral text-base" 
                  data-testid="hero-get-quote-btn"
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={() => scrollToTop("/packages")}
                  className="btn-secondary border-white text-white hover:bg-white hover:text-primary" 
                  data-testid="hero-explore-btn"
                >
                  Explore Packages
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/20">
                <div>
                  <span className="block text-3xl font-bold text-coral">500+</span>
                  <span className="text-white/70 text-sm">Destinations</span>
                </div>
                <div>
                  <span className="block text-3xl font-bold text-coral">10K+</span>
                  <span className="text-white/70 text-sm">Happy Travelers</span>
                </div>
                <div>
                  <span className="block text-3xl font-bold text-coral">8+</span>
                  <span className="text-white/70 text-sm">Years Experience (Since 2017)</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

      </section>

      {/* Featured Destinations */}
      <section className="section-padding bg-white" data-testid="featured-destinations">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="caption text-coral mb-4 block">Explore the World</span>
            <h2 className="heading-2 text-primary mb-4">Popular Destinations</h2>
            <p className="body-lg max-w-2xl mx-auto">
              From tropical paradises to cultural wonders, discover our handpicked destinations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...destinations.international.slice(0, 3), ...destinations.domestic.slice(0, 3)].map(
              (dest, index) => (
                <motion.div
                  key={dest.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div 
                    onClick={() => scrollToTop(`/packages?destination=${dest.name}`)} 
                    className="cursor-pointer"
                    data-testid={`destination-card-${dest.id}`}
                  >
                    <div className="card-destination aspect-[4/5] group">
                      <img
                        src={dest.image}
                        alt={dest.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
                          <MapPin className="h-4 w-4" />
                          {dest.country}
                        </div>
                        <h3 className="text-2xl font-heading font-semibold text-white mb-2">
                          {dest.name}
                        </h3>
                        <p className="text-white/70 text-sm">{dest.description}</p>
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-coral font-medium">
                            From ₹{dest.starting_price?.toLocaleString()}
                          </span>
                          <span className="text-white/60 group-hover:text-coral transition-colors">
                            Explore <ArrowRight className="inline h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            )}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <Button 
              onClick={() => scrollToTop("/destinations")}
              className="btn-primary" 
              data-testid="view-all-destinations-btn"
            >
              View All Destinations
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Popular Packages */}
      <section className="section-padding bg-accent" data-testid="popular-packages">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="caption text-coral mb-4 block">Curated Experiences</span>
            <h2 className="heading-2 text-primary mb-4">Popular Tour Packages</h2>
            <p className="body-lg max-w-2xl mx-auto">
              Handcrafted itineraries designed to give you the best travel experience
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="card-package h-full" data-testid={`package-card-${pkg.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={pkg.image}
                      alt={pkg.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-coral text-white text-xs font-medium rounded-full uppercase">
                        {pkg.category}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
                      <Clock className="h-4 w-4" />
                      {pkg.duration}
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-primary mb-2">
                      {pkg.title}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {pkg.highlights?.slice(0, 3).map((h) => (
                        <span
                          key={h}
                          className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div>
                        <span className="text-sm text-slate-500">Starting from</span>
                        <div className="text-2xl font-bold text-coral">
                          ₹{pkg.price?.toLocaleString()}
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="border-primary text-primary hover:bg-primary hover:text-white" 
                        onClick={() => scrollToTop(`/packages/${pkg.id}`)}
                        data-testid={`view-package-${pkg.id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <Button 
              onClick={() => scrollToTop("/packages")}
              className="btn-coral" 
              data-testid="view-all-packages-btn"
            >
              View All Packages
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white" data-testid="why-choose-us">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="caption text-coral mb-4 block">Our Promise</span>
            <h2 className="heading-2 text-primary mb-4">Why Choose Dnotours?</h2>
            <p className="body-lg max-w-2xl mx-auto">
              Experience the difference with our commitment to excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustFactors.map((factor, index) => (
              <motion.div
                key={factor.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-center p-6" data-testid={`trust-factor-${index}`}>
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-coral/10 flex items-center justify-center">
                    <factor.icon className="h-8 w-8 text-coral" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-primary mb-3">
                    {factor.title}
                  </h3>
                  <p className="text-slate-600">{factor.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-slate-100" data-testid="testimonials">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="caption text-coral mb-4 block">Testimonials</span>
            <h2 className="heading-2 text-primary mb-4">What Our Travelers Say</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real experiences from real travelers who trusted us with their journeys
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(reviews.length > 0 ? reviews.slice(0, 6) : testimonials).map((item, index) => {
              const name = item.customer_name || item.name;
              const text = item.review_text || item.text;
              const rating = item.rating || 5;
              const location = item.location || "";
              const initial = item.initial || name?.[0]?.toUpperCase() || "D";
              return (
                <motion.div
                  key={name + index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 h-full flex flex-col" data-testid={`testimonial-${index}`}>
                    <div className="flex gap-1 mb-4">
                      {[...Array(Math.floor(rating))].map((_, i) => (
                        <Star key={`star-${i}`} className="h-5 w-5 text-coral fill-coral" />
                      ))}
                      {rating % 1 !== 0 && (
                        <div className="relative">
                          <Star className="h-5 w-5 text-slate-200 fill-slate-200" />
                          <div className="absolute inset-0 overflow-hidden w-1/2">
                            <Star className="h-5 w-5 text-coral fill-coral" />
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-slate-600 mb-6 leading-relaxed flex-grow text-sm">"{text}"</p>
                    <div className="flex items-center gap-4">
                      {item.photo_url ? (
                        <img src={item.photo_url} alt={name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-coral to-red-500 flex items-center justify-center text-white font-bold text-lg">
                          {initial}
                        </div>
                      )}
                      <div>
                        <h4 className="font-semibold text-slate-800">{name}</h4>
                        <span className="text-sm text-slate-500">{location}{item.trip_destination ? ` • ${item.trip_destination}` : ''}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trending Packages */}
      {trendingPkgs.length > 0 && (
        <section className="section-padding bg-white" data-testid="trending-section">
          <div className="container-custom">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <span className="caption text-coral mb-4 block">Popular Right Now</span>
              <h2 className="heading-2 text-primary mb-4">Trending Packages</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">Most viewed and enquired packages by our travelers</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {trendingPkgs.map((pkg, i) => (
                <motion.div key={pkg.id || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <Link to={`/packages/${pkg.id}`}>
                    <Card className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer" data-testid={`trending-pkg-${i}`}>
                      <img src={pkg.images?.[0] || "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400"} alt={pkg.title} className="w-full h-40 object-cover" />
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-slate-800 text-sm line-clamp-1">{pkg.title}</h4>
                        <p className="text-xs text-slate-500 mt-1">{pkg.destination} • {pkg.duration}</p>
                        <p className="text-red-600 font-bold mt-2 text-sm">{pkg.price ? `₹${pkg.price.toLocaleString()}` : 'On Request'}</p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Corporate Section */}
      <section className="section-padding bg-accent" data-testid="corporate-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="caption text-coral mb-4 block">Corporate Travel</span>
              <h2 className="heading-2 text-primary mb-6">Corporate & MICE Solutions</h2>
              <p className="body-lg mb-6">
                Elevate your business travel and events with our comprehensive corporate solutions. From executive travel to large-scale conferences, we deliver excellence.
              </p>
              <ul className="space-y-4 mb-8">
                {["Corporate Travel Management", "Incentive Travel Programs", "Conferences & Events", "Team Building Retreats"].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-600">
                    <div className="w-2 h-2 bg-coral rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => scrollToTop("/corporate")}
                className="btn-primary" 
                data-testid="corporate-cta-btn"
              >
                Explore Corporate Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.pexels.com/photos/4940642/pexels-photo-4940642.jpeg?w=800"
                alt="Corporate Travel"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-coral relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <h2 className="heading-2 text-white mb-6">Ready to Start Your Journey?</h2>
            <p className="text-lg text-white/90 mb-8">
              Get in touch with our travel experts and let us craft the perfect itinerary for your next adventure.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => scrollToTop("/get-quote")}
                className="bg-white text-coral hover:bg-white/90 h-12 px-8 rounded-full font-medium text-base" 
                data-testid="cta-get-quote-btn"
              >
                Get Free Quote
              </Button>
              <a href="tel:+918369033791">
                <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-coral h-12 px-8 rounded-full font-medium" data-testid="cta-call-btn">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
